import { getCookie } from 'shared/libs/storage/cookie'
import { $RTKGlobalAPI } from 'shared/store'
import { Tags } from 'shared/store/types/api'
import { RequestMethod } from 'shared/types/Api'

import { apiRoutes } from 'shared/config/apiPath/apiPath'

import {
  Cart,
  ChangePaymentMethodArgs,
  CheckMbankPayArgs,
  GetBranchesAddressesArgs,
  GetBranchesAddressesResponse,
  GetCartOrderByIdResponse,
  GetDeliveriesResponse,
  GetDeliveryTimesResponse,
  GetMPlusByCartArgs,
  GetMPlusByCartResponse,
  GetTemporaryCartArgs,
  MbankPayOrderConfirmArgs,
  MbankPayPrepareOrderArgs,
  MbankPayPrepareOrderResponse,
  MplusPrepareOrderArgs,
  MplusPrepareOrderResponse,
  MutateAuthCartArgs,
  MutateTemporaryCartArgs,
  PaylerOrderPrepareArgs,
  PaylerOrderPrepareResponse,
  PrepareOrderApiArgs,
  RemoveProductFromTemporaryCartArgs,
  RemoveTemporaryCartArgs,
  SetDeliveryArgs,
  SetDeliveryMethodArgs,
  SetPickupFromArgs,
} from './types'

export const cartApi = $RTKGlobalAPI.injectEndpoints({
  endpoints: builder => ({
    // unauth cart
    getTemporaryCarts: builder.query<Cart[], string>({
      query: id => apiRoutes.cart.temporaryCarts(id),
    }),
    getTemporaryCartById: builder.query<Cart, GetTemporaryCartArgs>({
      query: ({ ownerId, cartPk }) =>
        apiRoutes.cart.temporaryCartById(ownerId, cartPk),
    }),
    removeProductFromTemporaryCart: builder.mutation<
      Cart,
      RemoveProductFromTemporaryCartArgs
    >({
      query: ({ ownerId, id }) => {
        return {
          url: apiRoutes.cart.removeProductFromTemporaryCart(ownerId, id),
          method: RequestMethod.DELETE,
        }
      },
    }),
    removeTemporaryCart: builder.mutation<void, RemoveTemporaryCartArgs>({
      query: ({ ownerId, cartPk }) => {
        return {
          url: apiRoutes.cart.removeTemporaryCart(ownerId, cartPk),
          method: RequestMethod.DELETE,
        }
      },
    }),
    mutateTemporaryCart: builder.mutation<Cart, MutateTemporaryCartArgs>({
      query: ({ ownerId, ...body }) => {
        return {
          url: apiRoutes.cart.mutateTemporaryCart(ownerId),
          method: RequestMethod.POST,
          body,
        }
      },
    }),
    updateCartToReal: builder.mutation<void, string>({
      query: ownerId => {
        return {
          url: apiRoutes.cart.updateCartToReal(ownerId),
          method: RequestMethod.POST,
        }
      },
    }),

    //auth cart
    getAuthCarts: builder.query<Cart[], void>({
      query: () => apiRoutes.cart.authCarts,
    }),
    getAuthCartById: builder.query<Cart, number>({
      query: cartPk => apiRoutes.cart.authCartById(cartPk),
    }),
    removeProductFromAuthCart: builder.mutation<Cart, number>({
      query: id => {
        return {
          url: apiRoutes.cart.removeProductFromAuthCart(id),
          method: RequestMethod.DELETE,
        }
      },
    }),
    mutateAuthCart: builder.mutation<Cart, MutateAuthCartArgs>({
      query: body => {
        return {
          url: apiRoutes.cart.mutateAuthCart,
          method: RequestMethod.POST,
          body,
        }
      },
    }),
    removeAuthCart: builder.mutation<void, number>({
      query: cartPk => {
        return {
          url: apiRoutes.cart.removeAuthCart(cartPk),
          method: RequestMethod.DELETE,
        }
      },
    }),
    getCartOrderById: builder.query<GetCartOrderByIdResponse, string>({
      query: id => {
        const isWebview = getCookie('isWebview')
        return {
          url: apiRoutes.cart.byId(id),
          params: {
            platform: isWebview ? 'webview' : 'web',
          },
        }
      },
      providesTags: [Tags.CART],
    }),
    getDeliveryTimes: builder.query<GetDeliveryTimesResponse, string>({
      query: id => ({
        url: apiRoutes.cart.getDeliveryTimesById(id),
      }),
    }),
    getDeliveries: builder.query<GetDeliveriesResponse, string>({
      query: id => ({
        url: apiRoutes.cart.getDeliveries(id),
      }),
      providesTags: [Tags.DELIVERY],
    }),
    getCheckMplusById: builder.query<GetCartOrderByIdResponse, string>({
      query: id => {
        return {
          url: apiRoutes.cart.checkMplus(id),
        }
      },
    }),
    setDelivery: builder.mutation<GetCartOrderByIdResponse, SetDeliveryArgs>({
      query: ({ id, deliveryId }) => {
        return {
          url: apiRoutes.cart.setDelivery(id),
          method: RequestMethod.POST,
          body: {
            delivery: deliveryId,
          },
        }
      },
      invalidatesTags: [Tags.CART],
    }),
    setDeliveryMethod: builder.mutation<
      GetCartOrderByIdResponse,
      SetDeliveryMethodArgs
    >({
      query: ({ id, delivery_method }) => {
        return {
          url: apiRoutes.cart.setDeliveryMethod(id),
          method: RequestMethod.POST,
          body: {
            delivery_method,
          },
        }
      },
      invalidatesTags: [Tags.CART],
    }),
    orderPrepare: builder.mutation<void, PrepareOrderApiArgs>({
      query: body => {
        return {
          url: apiRoutes.cart.prepareOrder,
          method: RequestMethod.POST,
          body,
        }
      },
    }),
    mplusPrepareOrder: builder.mutation<
      MplusPrepareOrderResponse,
      MplusPrepareOrderArgs
    >({
      query: body => ({
        url: apiRoutes.cart.mplusPrepareOrder,
        method: RequestMethod.POST,
        body,
      }),
    }),
    checkMbankPay: builder.mutation<void, CheckMbankPayArgs>({
      query: ({ id, ...body }) => ({
        url: apiRoutes.cart.checkMbankPay(id),
        method: RequestMethod.POST,
        body,
      }),
    }),
    mbankPayPrepareOrder: builder.mutation<
      MbankPayPrepareOrderResponse,
      MbankPayPrepareOrderArgs
    >({
      query: ({ order_id, ...body }) => ({
        url: apiRoutes.cart.mbankPayPrepareOrder(String(order_id)),
        method: RequestMethod.POST,
        body,
      }),
    }),
    mbankPayOrderConfirm: builder.mutation<void, MbankPayOrderConfirmArgs>({
      query: ({ id, ...body }) => ({
        url: apiRoutes.cart.mbankPayOrderConfirm(id),
        method: RequestMethod.POST,
        body,
      }),
    }),
    paylerOrderPrepare: builder.mutation<
      PaylerOrderPrepareResponse,
      PaylerOrderPrepareArgs
    >({
      query: ({ order_id, ...body }) => ({
        url: apiRoutes.cart.paylerOrderPrepare(order_id),
        method: RequestMethod.POST,
        body,
      }),
    }),
    mplusDetails: builder.query<GetMPlusByCartResponse, GetMPlusByCartArgs>({
      query: cartId => apiRoutes.cart.getMPlusByCart(cartId),
    }),
    changePaymentMethod: builder.mutation<void, ChangePaymentMethodArgs>({
      query: ({ cartId, payment_method }) => ({
        url: apiRoutes.cart.changePaymentMethod(cartId),
        method: 'POST',
        body: {
          payment_method,
        },
      }),
      invalidatesTags: [Tags.CART],
    }),
    branchesAddresses: builder.query<
      GetBranchesAddressesResponse,
      GetBranchesAddressesArgs
    >({
      query: ({ cartId, url }) =>
        apiRoutes.cart.getBranchesAddressesByCart(cartId, url),
    }),
    setPickupFrom: builder.mutation<
      GetCartOrderByIdResponse,
      SetPickupFromArgs
    >({
      query: ({ id, pick_up_from }) => {
        return {
          url: apiRoutes.cart.setPickupFrom(id),
          method: RequestMethod.POST,
          body: {
            pick_up_from,
          },
        }
      },
      invalidatesTags: [Tags.CART],
    }),
  }),
})

export const {
  useLazyGetAuthCartByIdQuery,
  useGetCartOrderByIdQuery,
  useLazyGetCartOrderByIdQuery,
  useGetCheckMplusByIdQuery,
  useSetDeliveryMethodMutation,
  useSetDeliveryMutation,
  useGetDeliveryTimesQuery,
  useGetDeliveriesQuery,
  useLazyGetTemporaryCartsQuery,
  useRemoveTemporaryCartMutation,
  useRemoveAuthCartMutation,
  useLazyGetTemporaryCartByIdQuery,
  useRemoveProductFromTemporaryCartMutation,
  useOrderPrepareMutation,
  useMplusPrepareOrderMutation,
  useMutateTemporaryCartMutation,
  useCheckMbankPayMutation,
  useMbankPayPrepareOrderMutation,
  useMbankPayOrderConfirmMutation,
  usePaylerOrderPrepareMutation,
  useMplusDetailsQuery,
  useUpdateCartToRealMutation,
  useLazyGetAuthCartsQuery,
  useMutateAuthCartMutation,
  useRemoveProductFromAuthCartMutation,
  useChangePaymentMethodMutation,
  useLazyBranchesAddressesQuery,
  useLazyGetDeliveriesQuery,
  useSetPickupFromMutation,
  endpoints: cartEndpoints,
} = cartApi
